import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Clock, Users, ArrowUpRight } from "lucide-react";

const TOURS_BASE = [
  {
    id: "sunset-cruise",
    price: "€120",
    img: "https://customer-assets.emergentagent.com/job_vivendi-sailing/artifacts/2t46gwdi_IMG_20260530_190358_170.webp",
    pos: "object-bottom",
    keys: { name: "tours.short_name", duration: "tours.short_dur", desc: "tours.short_desc" },
  },
  {
    id: "bay-of-bones",
    price: "€220",
    img: "https://customer-assets.emergentagent.com/job_vivendi-sailing/artifacts/pw54bm9p_IMG_20260530_173646_615.webp",
    pos: "object-bottom",
    keys: { name: "tours.half_name", duration: "tours.half_dur", desc: "tours.half_desc" },
  },
  {
    id: "full-day-sail",
    price: "€450",
    img: "/tours/full-day-sail-clean.png",
    filter: "brightness(1.04) contrast(1.04) saturate(1.04)",
    keys: { name: "tours.full_name", duration: "tours.full_dur", desc: "tours.full_desc" },
  },
];

export default function Tours() {
  const { t } = useTranslation();
  const scrollToBooking = (tourId) => {
    const el = document.getElementById("booking");
    if (el) el.scrollIntoView({ behavior: "smooth" });
    window.dispatchEvent(new CustomEvent("prefill-tour", { detail: tourId }));
  };

  return (
    <section id="tours" data-testid="tours-section" className="py-24 lg:py-36 bg-[#FAF7F2]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-px bg-[#0A1B35]" />
              <span className="overline text-[#0A1B35]">{t("tours.eyebrow")}</span>
            </div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#0A1B35] leading-[1.05]">
              {t("tours.title_pre")} <em className="italic">{t("tours.title_em")}</em>
              {t("tours.title_dot")}
            </h2>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-10 lg:gap-16">
          {TOURS_BASE.map((tour, i) => {
            const name = t(tour.keys.name);
            const duration = t(tour.keys.duration);
            const desc = t(tour.keys.desc);
            return (
              <motion.article
                key={tour.id}
                data-testid={`tour-card-${tour.id}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.8, delay: i * 0.08 }}
                className="group"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={tour.img}
                    alt={name}
                    style={tour.filter ? { filter: tour.filter } : undefined}
                    className="block w-full h-auto transition-transform duration-[1200ms] group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4 bg-white/90 backdrop-blur px-3 py-1.5">
                    <span className="overline text-[#0A1B35] text-[0.65rem]">{duration}</span>
                  </div>
                  {tour.price && (
                    <div className="absolute top-4 right-4 bg-[#0A1B35] text-white px-3 py-1.5">
                      <span className="overline text-[#D1BFAe] text-[0.65rem]">{tour.price}</span>
                    </div>
                  )}
                </div>

                <div className="mt-6 flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-serif-display text-3xl text-[#0A1B35]">{name}</h3>
                    <div className="flex items-center gap-5 mt-2 text-[#4B5563]">
                      <span className="flex items-center gap-1.5 text-xs">
                        <Clock className="w-3.5 h-3.5" strokeWidth={1.4} />
                        {duration}
                      </span>
                      <span className="flex items-center gap-1.5 text-xs">
                        <Users className="w-3.5 h-3.5" strokeWidth={1.4} />
                        {t("tours.max_guests")}
                      </span>
                    </div>
                  </div>
                  <button
                    data-testid={`tour-book-${tour.id}`}
                    onClick={() => scrollToBooking(tour.id)}
                    className="shrink-0 w-11 h-11 rounded-full bg-[#0A1B35] text-white flex items-center justify-center hover:bg-[#D1BFAe] hover:text-[#0A1B35] transition-all duration-300"
                    aria-label={t("tours.book_aria", { name })}
                  >
                    <ArrowUpRight className="w-5 h-5" strokeWidth={1.5} />
                  </button>
                </div>

                <p className="mt-4 text-[#4B5563] font-light leading-relaxed max-w-md">
                  {desc}
                </p>
              </motion.article>
            );
          })}
        </div>

        <div className="mt-16 lg:mt-20 border-t border-[#0A1B35]/10 pt-10 grid md:grid-cols-3 gap-10 lg:gap-16">
          <div className="md:col-span-2">
            <div className="overline text-[#0A1B35] mb-4">{t("tours.tailor_title")}</div>
            <p className="text-[#0A1B35]/85 font-light leading-[1.7] text-base lg:text-lg max-w-2xl">
              {t("tours.tailor_body")}
            </p>
          </div>
          <div>
            <div className="overline text-[#0A1B35] mb-3">{t("tours.weather_title")}</div>
            <p className="text-[#4B5563] font-light leading-relaxed text-sm">
              {t("tours.weather_body")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// Light-weight static list for BookingForm prefill / select
export const TOURS = TOURS_BASE.map((t) => ({
  id: t.id,
  nameKey: t.keys.name,
  durationKey: t.keys.duration,
  price: t.price,
}));
